'use client';

import { submitMerchForm } from '@/lib/supabase/form-actions'
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Loader2 } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { merchFormSchema, type MerchFormData } from '@/lib/types/forms';
import { captureAnalyticsEvent } from '@/lib/analytics';
import { buildMerchWhatsAppMessage, buildWhatsAppUrl } from '@/lib/links';
import { toast } from 'sonner';

interface MerchFormDialogProps {
  trigger: React.ReactNode;
  onSuccess?: () => void;
  onError?: (error: Error) => void;
}

export function MerchFormDialog({ trigger, onSuccess, onError }: MerchFormDialogProps) {
  const [open, setOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const form = useForm<MerchFormData>({
    resolver: zodResolver(merchFormSchema),
    defaultValues: {
      name: '',
      email: '',
      phone: '',
      quantity: '',
      message: '',
    },
  });

  const onSubmit = async (data: MerchFormData) => {
    captureAnalyticsEvent('inquiry_submitted', { inquiry_type: 'merch' });
    setIsSubmitting(true);
    try {
      await submitMerchForm(data);
      captureAnalyticsEvent('inquiry_submission_succeeded', { inquiry_type: 'merch' });
      captureAnalyticsEvent('booking_intent_clicked', { inquiry_type: 'merch', channel: 'whatsapp' });
      window.open(
        buildWhatsAppUrl(buildMerchWhatsAppMessage(data)),
        '_blank',
        'noopener,noreferrer',
      );
      toast.success('Opening WhatsApp', {
        description: 'Your enquiry is saved — send the message to confirm with us.',
      });
      setOpen(false);
      form.reset();
      onSuccess?.();
    } catch (error) {
      captureAnalyticsEvent('inquiry_submission_failed', { inquiry_type: 'merch' });
      onError?.(error as Error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleOpenChange = (nextOpen: boolean) => {
    if (nextOpen && !open) {
      captureAnalyticsEvent('inquiry_opened', { inquiry_type: 'merch' });
    }
    setOpen(nextOpen);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Ask about the Experience Calendar</DialogTitle>
          <p className="text-sm text-muted-foreground mt-2">
            Share your quantity and contact details. We will confirm stock, payment and dispatch before an order is placed.
          </p>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Name *</FormLabel>
                  <FormControl>
                    <Input placeholder="Enter your name" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Email</FormLabel>
                  <FormControl>
                    <Input type="email" placeholder="your@email.com" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="phone"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Phone *</FormLabel>
                  <FormControl>
                    <Input type="tel" placeholder="Enter phone number" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="quantity"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Quantity *</FormLabel>
                  <FormControl>
                    <Input type="number" min="1" step="1" placeholder="Enter quantity" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="eventType"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Buying for *</FormLabel>
                  <Select onValueChange={field.onChange} value={field.value}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Select a purpose" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value="family_functions">Family Functions</SelectItem>
                      <SelectItem value="corporate_events">Corporate Events</SelectItem>
                      <SelectItem value="personal_gifting">Personal Gifting</SelectItem>
                      <SelectItem value="personal_use">Myself</SelectItem>
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="message"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Message (Optional)</FormLabel>
                  <FormControl>
                    <Textarea placeholder="Tell us about your requirements..." rows={4} {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <Button type="submit" className="w-full" disabled={isSubmitting}>
              {isSubmitting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Submitting...
                </>
              ) : (
                'Send enquiry'
              )}
            </Button>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
